import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  DatePicker
} from '@mui/x-date-pickers/DatePicker';
import {
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import axiosInstance from '../utils/axios';

const CourierStats = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }

      const response = await axiosInstance.get(`/courier-stats?${params.toString()}`);
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleDateFilter = () => {
    fetchStats();
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setTimeout(fetchStats, 100);
  };

  const handleViewDetails = (courierId) => {
    navigate(`/courier-stats/${courierId}`, {
      state: { startDate, endDate }
    });
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 80) return 'warning';
    return 'error';
  };

  if (loading && !data) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          Загрузка статистики курьеров...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Статистика курьеров
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/courier-salaries')}
          startIcon={<PersonIcon />}
        >
          Зарплаты
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Фильтры по датам */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Фильтры
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Дата начала"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="dd.MM.yyyy"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Дата окончания"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="dd.MM.yyyy"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={handleDateFilter}
              disabled={loading}
              fullWidth
            >
              Применить
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={handleClearFilter}
              disabled={loading}
              fullWidth
            >
              Сбросить
            </Button>
          </Grid>
        </Grid>
        {(startDate || endDate) && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Период: {startDate ? format(startDate, 'dd.MM.yyyy', { locale: ru }) : '...'} - {endDate ? format(endDate, 'dd.MM.yyyy', { locale: ru }) : '...'}
          </Typography>
        )}
      </Paper>

      {data && (
        <>
          {/* Общая статистика */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <CancelIcon color="error" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Ненаходы (курьеры)
                      </Typography>
                      <Typography variant="h5" component="div">
                        {data.totalStats.totalNotFound}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <CancelIcon color="warning" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Всего ненаходов за период
                      </Typography>
                      <Typography variant="h5" component="div">
                        {data.totalStats.totalNotFoundPeriod || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      {data.totalStats.totalCouriers}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Всего курьеров
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AssignmentIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="h6">
                      {data.totalStats.totalOrders}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Всего заказов
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">
                      {data.totalStats.overallSuccessRate}%
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Общий успех
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">
                      {data.totalStats.totalRevenue}₽
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Общая выручка
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Таблица курьеров */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ p: 2 }}>
              Детальная статистика по курьерам
            </Typography>
            
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Курьер</TableCell>
                    <TableCell align="right">Заказов</TableCell>
                    <TableCell align="right">Успешно</TableCell>
                    <TableCell align="right">Ненаходы</TableCell>
                    <TableCell align="right">% Успеха</TableCell>
                    <TableCell align="right">Выручка</TableCell>
                    <TableCell align="right">Средний чек</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.couriers.map((courier) => (
                    <TableRow key={courier.courierId} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {courier.courierName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            @{courier.courierUsername}
                          </Typography>
                          {courier.courierTelegram && (
                            <Typography variant="caption" color="text.secondary">
                              TG: {courier.courierTelegram}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="h6">
                          {courier.totalOrders}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <CheckCircleIcon sx={{ mr: 0.5, color: 'success.main', fontSize: 16 }} />
                          {courier.successfulOrders}
                        </Box>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <CancelIcon sx={{ mr: 0.5, color: 'error.main', fontSize: 16 }} />
                          {courier.notFoundOrders}
                        </Box>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Chip
                          label={`${courier.successRate}%`}
                          color={getSuccessRateColor(courier.successRate)}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {courier.totalRevenue}₽
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2">
                          {courier.avgOrderValue}₽
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Tooltip title="Подробная статистика">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(courier.courierId)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {data.couriers.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Нет данных для отображения
                </Typography>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CourierStats;
